import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { schoolClassSortOrder } from '../services/TimetableService';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import './AdminPerformanceView.css';
import './StudentDetailPages.css';
import { scopeForAdmin, schoolMatches } from '../utils/schoolScope';

function SuperAdminPerformanceView({ adminData }) {
  const [saSchoolId, setSaSchoolId] = useState('');
  const [saSchools, setSaSchools] = useState([]);
  useEffect(() => {
    getDocs(collection(db, 'schools')).then(snap => setSaSchools(snap.docs.map(d => ({ id: d.id, ...d.data() })))).catch(() => {});
  }, []);
  adminData = useMemo(() => ({ ...(adminData || {}), schoolId: saSchoolId || adminData?.schoolId || null }), [adminData, saSchoolId]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [grades, setGrades] = useState([]);
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadClasses = useCallback(async () => {
    console.log('🔥 AdminPerformance: Loading classes, schoolId:', adminData?.schoolId);
    try {
      const snapshot = await getDocs(collection(db, 'classes'));
      let classesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      classesData = scopeForAdmin(classesData, adminData);
      console.log('🔥 AdminPerformance: Classes loaded', classesData.length, classesData);
      classesData.sort(schoolClassSortOrder);
      setClasses(classesData);
    } catch (error) {
      console.error('🔥 AdminPerformance: Error loading classes:', error);
    }
  }, [adminData]);

  const loadStudents = useCallback(async () => {
    console.log('🔥 AdminPerformance: Loading students');
    try {
      const studentsData = [];
      for (const cls of classes) {
        const classStudentsQuery = query(
          collection(db, 'classes', cls.id, 'students'),
          where('isActive', '==', true)
        );
        const snapshot = await getDocs(classStudentsQuery);
        snapshot.docs.forEach(doc => {
          studentsData.push({ id: doc.id, ...doc.data() });
        });
      }
      console.log('🔥 AdminPerformance: Students loaded', studentsData.length, studentsData);
      setStudents(studentsData);
    } catch (error) {
      console.error('🔥 AdminPerformance: Error loading students:', error);
    } finally {
      setLoading(false);
    }
  }, [classes]);

  const loadGrades = useCallback(async () => {
    console.log('🔥 AdminPerformance: Loading grades');
    try {
      const gradesData = [];
      for (const cls of classes) {
        const assignmentsSnapshot = await getDocs(collection(db, 'classes', cls.id, 'assignments'));
        for (const assignmentDoc of assignmentsSnapshot.docs) {
          const gradesSnapshot = await getDocs(
            collection(db, 'classes', cls.id, 'assignments', assignmentDoc.id, 'grades')
          );
          gradesSnapshot.docs.forEach(doc => {
            gradesData.push({ id: doc.id, ...doc.data() });
          });
        }
      }
      console.log('🔥 AdminPerformance: Grades loaded', gradesData.length, gradesData);
      setGrades(gradesData);
    } catch (error) {
      console.error('🔥 AdminPerformance: Error loading grades:', error);
    }
  }, [classes]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  useEffect(() => {
    if (classes.length > 0) {
      loadStudents();
      loadGrades();
    }
  }, [classes, loadStudents, loadGrades]);

  const getFilteredStudents = () => {
    if (selectedClass === 'all') return students;
    return students.filter(student => student.classId === selectedClass);
  };

  const calculateStudentAverage = (studentId) => {
    const studentGrades = grades.filter(grade => grade.studentId === studentId);
    if (studentGrades.length === 0) return 0;
    const sum = studentGrades.reduce((acc, grade) => acc + (grade.score / grade.maxScore) * 100, 0);
    return (sum / studentGrades.length).toFixed(1);
  };

  const getPerformanceTier = (average) => {
    if (average >= 90) return { tier: 'Excellent', color: '#22c55e', letter: 'A' };
    if (average >= 80) return { tier: 'Good', color: '#22c55e', letter: 'B' };
    if (average >= 70) return { tier: 'Satisfactory', color: '#f59e0b', letter: 'C' };
    if (average >= 60) return { tier: 'Needs Improvement', color: '#f97316', letter: 'D' };
    return { tier: 'Critical', color: '#ef4444', letter: 'F' };
  };

  const getPerformanceDistribution = (studentsWithPerf) => {
    const tiers = { Excellent: 0, Good: 0, Satisfactory: 0, 'Needs Improvement': 0, Critical: 0 };
    studentsWithPerf.forEach(s => {
      const tier = s.performance.tier;
      if (tiers[tier] !== undefined) tiers[tier]++;
    });
    return [
      { name: 'Excellent (90+)', value: tiers.Excellent, color: '#22c55e' },
      { name: 'Good (80-89)', value: tiers.Good, color: '#22c55e' },
      { name: 'Satisfactory (70-79)', value: tiers.Satisfactory, color: '#f59e0b' },
      { name: 'Needs Improvement (60-69)', value: tiers['Needs Improvement'], color: '#f97316' },
      { name: 'Critical (<60)', value: tiers.Critical, color: '#ef4444' },
    ].filter(d => d.value > 0);
  };

  const getClassAverages = () => {
    return classes.map(cls => {
      const classStudents = students.filter(s => s.classId === cls.id);
      if (classStudents.length === 0) return { name: cls.name, average: 0, students: 0 };
      const averages = classStudents.map(s => parseFloat(calculateStudentAverage(s.id)));
      const classAvg = averages.reduce((a, b) => a + b, 0) / averages.length;
      return { name: cls.name, average: parseFloat(classAvg.toFixed(1)), students: classStudents.length };
    }).filter(c => c.students > 0).sort((a, b) => b.average - a.average);
  };

  const schoolNameFor = (sid) => saSchools.find(s => schoolMatches(s.id, sid))?.name || sid || '';
  const schoolNameForStudent = (student) => schoolNameFor(classes.find(c => c.id === student?.classId)?.schoolId);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (selectedStudent) {
    console.log('🔥 SuperAdminPerformance: Opened student detail', selectedStudent.id);
    return (
      <div className="detail-page">
        <button className="detail-back-btn" onClick={() => setSelectedStudent(null)}>◄ Back</button>
        <div className="detail-hero">
          <div className="detail-hero-icon">{selectedStudent.firstName ? selectedStudent.firstName[0] : 'S'}</div>
          <div>
            <h2 className="detail-page-title">
              {selectedStudent.fullName || `${selectedStudent.firstName || ''} ${selectedStudent.lastName || ''}`.trim() || 'Unknown'}
              <span className="detail-badge" style={{ background: `${selectedStudent.performance.color}20`, color: selectedStudent.performance.color }}>{selectedStudent.performance.tier}</span>
            </h2>
            <p className="detail-page-subtitle">Student ID: {selectedStudent.studentId || 'N/A'}</p>
          </div>
        </div>

        <div className="detail-card">
          <h4>Student Details</h4>
          <div className="detail-rows">
            <div className="detail-row"><span className="detail-label">School</span><span className="detail-value">{schoolNameForStudent(selectedStudent) || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Class</span><span className="detail-value">{classes.find(c => c.id === selectedStudent.classId)?.name || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Average</span><span className="detail-value" style={{ color: selectedStudent.performance.color }}>{selectedStudent.average}%</span></div>
            <div className="detail-row"><span className="detail-label">Performance</span><span className="detail-value" style={{ color: selectedStudent.performance.color }}>{selectedStudent.performance.tier}</span></div>
            <div className="detail-row"><span className="detail-label">Total Grades</span><span className="detail-value">{selectedStudent.studentGrades.length}</span></div>
          </div>
        </div>

        <div className="detail-card">
          <h4>Recent Grades</h4>
          {selectedStudent.studentGrades.length === 0 ? (
            <p className="detail-note">No grades recorded</p>
          ) : (
            <div className="grades-list">
              {selectedStudent.studentGrades.map((grade, index) => {
                const percentage = (grade.score / grade.maxScore) * 100;
                return (
                  <div key={index} className="grade-item">
                    <span className="grade-score">{grade.score}/{grade.maxScore}</span>
                    <span className="grade-percentage">{percentage.toFixed(1)}%</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  const filteredStudents = getFilteredStudents();
  const studentsWithPerformance = filteredStudents.map(student => {
    const average = calculateStudentAverage(student.id);
    const performance = getPerformanceTier(parseFloat(average));
    const studentGrades = grades.filter(grade => grade.studentId === student.id);
    return { ...student, average, performance, studentGrades };
  }).sort((a, b) => b.average - a.average);

  return (
    <div className="admin-performance-view">
      <div className="view-header">
        <h2>Performance Analytics</h2>
        <div className="sa-school-filter"><label>School:</label><select value={saSchoolId} onChange={(e) => setSaSchoolId(e.target.value)}><option value="">All Schools</option>{saSchools.map(s => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}</select></div>
      </div>

      <div className="filter-bar">
        <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}>
          <option value="all">All Classes</option>
          {classes.map(cls => (
            <option key={cls.id} value={cls.id}>{cls.name}</option>
          ))}
        </select>
      </div>

      <div className="stats-section">
        <h3>Performance Overview</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-info">
              <h3>{studentsWithPerformance.length}</h3>
              <p>Total Students</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-info">
              <h3>{studentsWithPerformance.filter(s => s.average >= 70).length}</h3>
              <p>Performing Well</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⚠️</div>
            <div className="stat-info">
              <h3>{studentsWithPerformance.filter(s => s.average < 70).length}</h3>
              <p>Needs Attention</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      {studentsWithPerformance.length > 0 && (
        <div className="charts-section">
          {/* Performance Distribution Pie Chart */}
          <div className="chart-card">
            <h3>Performance Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={getPerformanceDistribution(studentsWithPerformance)}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {getPerformanceDistribution(studentsWithPerformance).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Class Averages Bar Chart */}
          {getClassAverages().length > 0 && (
            <div className="chart-card">
              <h3>Class Averages Comparison</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={getClassAverages()} margin={{ top: 20, right: 30, left: 0, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-30} textAnchor="end" height={70} interval={0} tick={{ fontSize: 12 }} />
                  <YAxis domain={[0, 100]} label={{ value: 'Average %', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Bar dataKey="average" fill="#22c55e" radius={[4, 4, 0, 0]} name="Class Average %" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {studentsWithPerformance.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h3>No Performance Data</h3>
          <p>No students found or no grades recorded</p>
        </div>
      ) : (
        <div className="performance-list">
          {studentsWithPerformance.map(student => (
            <div key={student.id} className="performance-card" onClick={() => setSelectedStudent(student)}>
              <div className="student-avatar">
                {student.firstName ? student.firstName[0] : 'S'}
              </div>
              <div className="performance-info">
                <h4>{student.fullName || `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Unknown'}</h4>
                <p>Student ID: {student.studentId}</p>
                <p>Class: {classes.find(c => c.id === student.classId)?.name || 'N/A'}</p>
                {schoolNameForStudent(student) && <p>🏫 {schoolNameForStudent(student)}</p>}
              </div>
              <div className="performance-metrics">
                <div className="average-score">
                  <span className="score-value">{student.average}%</span>
                  <span className="score-label">Average</span>
                </div>
                <div className="performance-tier" style={{ background: `${student.performance.color}20`, color: student.performance.color }}>
                  {student.performance.tier}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SuperAdminPerformanceView;
